import React, {
  InputHTMLAttributes,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import { IconBaseProps } from 'react-icons';
import { FaExclamationTriangle, FaEye, FaEyeSlash } from 'react-icons/fa';
import { useField } from '@unform/core';

import {
  phoneMasking,
  currencyMasking,
  cnpjMasking,
  cpfMasking,
  cepMasking,
  numberMasking,
  onlyNumbers,
  nameMasking,
  alphaMasking,
  allowZeroMasking,
  alphaMaskingExtra,
} from '../Input/masks';

import { Container, Error, ViewPass } from './styles';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  mask?:
    | 'phone'
    | 'cellphone'
    | 'currency'
    | 'cnpj'
    | 'cpf'
    | 'cep'
    | 'number'
    | 'onlyNumbers'
    | 'allowZero'
    | 'name'
    | 'alpha'
    | 'alphaExtra';
  disableErrorIcon?: boolean;
  name: string;
  icon?: React.ComponentType<IconBaseProps>;
  iconStyle?: object;
  isPass?: boolean;
  containerStyle?: object;
  inputStyle?: object;
  isPhone?: boolean;
  isDate?: boolean;
  isTime?: boolean;
  isHidden?: boolean;
}

const Input: React.FC<InputProps> = ({
  disableErrorIcon = false,
  name,
  icon: Icon,
  iconStyle = {},
  isPass,
  isDate,
  isTime,
  containerStyle = {},
  inputStyle = {},
  mask,
  isHidden = false,
  ...rest
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isFilled, setIsFilled] = useState(false);
  const [visible, setIsVisible] = useState(false);
  const { fieldName, defaultValue, error, registerField } = useField(name);
  const [maskError, setMaskError] = useState('');

  const handlePasswordVisibility = useCallback(() => {
    const check = inputRef.current;

    if (check) {
      if (check.type === 'password') {
        check.type = 'text';
      } else {
        check.type = 'password';
      }
    }

    setIsVisible(!visible);
  }, [visible]);

  const handleFocus = useCallback(() => {
    setMaskError('');

    const check = inputRef.current;

    if (check && isDate) {
      check.type = 'date';
      // check.click();
    }

    if (check && isTime) {
      check.type = 'time';
      // check.click();
    }

    setIsFocused(true);
  }, [isDate, isTime]);

  const handleBlur = useCallback(async () => {
    const check = inputRef.current;
    setIsFocused(false);

    if (check) {
      if ((isDate || isTime) && !check.value) {
        check.type = 'text';
      }
    }

    setIsFilled(!!inputRef.current?.value);
    const input = inputRef.current;

    if (input) {
      let message = '';
      if (input.value === '') {
        setMaskError('');
        return;
      }
      if (mask === 'cep') {
        if (input.value.length !== 9 && !!input.value) {
          input.value = '';
          message = 'É necessário os 8 digitos do CEP';
        }
      }

      if (mask === 'cnpj') {
        if (input.value.length < 14 && !!input.value) {
          input.value = '';
          message = 'É necessário os 18 digitos do CNPJ';
        }
      }

      if (mask === 'phone') {
        if (input.value.length < 14 && !!input.value) {
          input.value = '';
          message = 'Número de telefone inválido';
        }
      }

      setMaskError(message);
    }
  }, [mask, isDate, isTime]);

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef.current,
      path: 'value',
    });
  }, [registerField, fieldName]);

  const maskIt = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      if (mask === 'phone') {
        phoneMasking(e);
      }

      if (mask === 'currency') {
        currencyMasking(e);
      }

      if (mask === 'cnpj') {
        cnpjMasking(e);
      }

      if (mask === 'cpf') {
        cpfMasking(e);
      }

      if (mask === 'cep') {
        cepMasking(e);
      }

      if (mask === 'number') {
        numberMasking(e);
      }

      if (mask === 'onlyNumbers') {
        onlyNumbers(e);
      }

      if (mask === 'allowZero') {
        allowZeroMasking(e);
      }

      if (mask === 'name') {
        nameMasking(e);
      }

      if (mask === 'alpha') {
        alphaMasking(e);
      }
      if (mask === 'alphaExtra') {
        alphaMaskingExtra(e);
      }
    },
    [mask],
  );

  return (
    <Container
      $hasError={!!error}
      $isFocused={isFocused}
      $isFilled={isFilled}
      $isHidden={isHidden}
      style={containerStyle}
    >
      {Icon && <Icon style={iconStyle} />}
      <input
        style={inputStyle}
        name={name}
        onFocus={handleFocus}
        onBlur={handleBlur}
        defaultValue={defaultValue}
        ref={inputRef}
        onKeyUp={maskIt}
        {...rest}
      />
      {isPass && (
        <ViewPass>
          {visible ? (
            <FaEyeSlash size={20} onClick={handlePasswordVisibility} />
          ) : (
            <FaEye size={20} onClick={handlePasswordVisibility} />
          )}
        </ViewPass>
      )}
      {!disableErrorIcon ? (
        <>
          {error && (
            <Error title={error}>
              <FaExclamationTriangle color="#c53030" size={20} />
            </Error>
          )}
          {!error && maskError && (
            <Error title={maskError}>
              <FaExclamationTriangle color="#c53030" size={20} />
            </Error>
          )}
        </>
      ) : null}
    </Container>
  );
};

export default Input;
